import { Component, OnInit, AfterViewInit, ElementRef, ViewChild, Input, OnChanges } from '@angular/core';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import MetisMenu from 'metismenujs';
import { Router, NavigationEnd, RouterModule } from '@angular/router';

import { HttpClient } from '@angular/common/http';

import { MENU } from './menu';
import { MenuItem } from './menu.model';
import { canOpenFeature } from '../../core/guards/report-access';
import { GrantsRepository } from '../../core/repositories/grants.repository';
import { EventsRepository } from '../../core/repositories/events.repository';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SimplebarAngularModule } from 'simplebar-angular';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';

/**
 * Pages kept in the app but taken OFF the sidebar. The routes still work and
 * any existing link still lands — they are simply not offered as navigation.
 * Statistics duplicates Analytics.
 *
 * Sessions and Contributions sat here from 2026-09-23 to 2026-10-10 as duplicate
 * views of what the programme sync owns. They are back: for an organizer
 * evaluating their preview in the console they are the receipt in detail —
 * every session and talk as it was read from the link. Their detail pages'
 * edits are lockedFields-protected (see ../../pages/events/locked-fields.ts),
 * so a sync never overwrites what someone changed there.
 */
export const SIDEBAR_HIDDEN_LABELS = new Set([
  'Statistics',
]);

/**
 * Shown to super-admins only: role management, the Users page, the app-wide
 * version gate and search spend are ours (those routes carry superAdminGuard too,
 * so the address is closed, not only the link). The reports are not listed here — they carry a `feature`
 * and follow the organizer's granted permissions.
 */
export const SIDEBAR_SUPERADMIN_LABELS = new Set([
  'Admins & Moderators',
  'App Version',
  'Users',
  'Preview Console',
  'Search Usage',
]);

/**
 * What an organizer evaluating a PREVIEW needs (2026-10-11): the programme as it was
 * read, the people and partners they may want to add, the venue. Q&A, Discussions,
 * Participants, Awards and Submitted papers are live-event work and are noise before
 * go-live, so on a preview the organizer's menu is this list and its headings; a
 * super-admin keeps the full menu (they moderate previews). The events list stays so
 * they can leave the preview.
 */
export const SIDEBAR_PREVIEW_LABELS = new Set([
  'MENUITEMS.EVENTS.LIST.TEXT',
  'Home',
  'Sessions',
  'Contributions',
  'Programme Changes',
  'Committees',
  'Sponsors',
  'Exhibitors',
  'Floor Maps',
  'Venue & nearby',
  'Conference Services',
  'Information cards',
]);

/**
 * The menu as this viewer should see it. Pure + exported so the visibility
 * rules are unit-testable without rendering the sidebar.
 *
 * A page with a `feature` is gated: a super-admin always sees it, an organizer
 * only when their grant on the current event holds its permission (`grant` is
 * that grant, null while it loads, with no event open, or with none).
 *
 * MENU is a FLAT list in which an `isTitle` entry governs the entries after it
 * until the next title, so removing entries can strand a heading over nothing
 * — an organizer would see a bare "Insights" with no links beneath it. Empty
 * headings are therefore dropped as part of the same pass.
 */
export function visibleMenu(
  menu: MenuItem[],
  isSuperAdmin: boolean,
  grant: Record<string, unknown> | null = null,
  isPreview = false,
): MenuItem[] {
  const previewOnly = isPreview && !isSuperAdmin;
  const allowed = menu
    .filter(item => !SIDEBAR_HIDDEN_LABELS.has(item.label ?? ''))
    .filter(item => isSuperAdmin || !SIDEBAR_SUPERADMIN_LABELS.has(item.label ?? ''))
    .filter(item => !item.feature || canOpenFeature(isSuperAdmin, grant, item.feature))
    .filter(item => !previewOnly || item.isTitle || SIDEBAR_PREVIEW_LABELS.has(item.label ?? ''));

  return allowed.filter((item, i) => {
    if (!item.isTitle) return true;
    for (let j = i + 1; j < allowed.length; j++) {
      if (allowed[j].isTitle) break;  // next section reached with nothing between
      return true;                    // at least one entry under this heading
    }
    return false;
  });
}

/**
 * Rewrite a canonical per-event menu link (`/events/<slug>`) to the
 * eventId-scoped route (`/events/<eventId>/<slug>`). Pure + exported so the
 * mapping is unit-testable without rendering the sidebar. Non-`/events/…`
 * links (or a missing eventId) are returned unchanged.
 */
export function eventScopedPath(link: string, eventId: string | null): string {
  if (!eventId || !link.startsWith('/events/')) return link;
  return link.replace(/^\/events\//, `/events/${eventId}/`);
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone:true,
  imports:[SimplebarAngularModule,RouterModule,CommonModule,TranslateModule ]
})

/**
 * Sidebar component
 */
export class SidebarComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('componentRef') scrollRef;
  @Input() isCondensed = false;
  menu: any;
  data: any;

  menuItems: MenuItem[] = [];

  eventId: string | null = null;

  @ViewChild('sideMenu') sideMenu: ElementRef;

  // The signed-in user's grant on the open event, for the gated entries; null
  // until it loads. Keyed by event AND user: signing in as someone else on
  // the same event must not keep the previous user's reports.
  private grant: Record<string, unknown> | null = null;
  private grantLoadedFor: string | null = null;
  private uid: string | null = null;

  // Whether the open event is a self-serve preview, for the reduced organizer menu;
  // loaded once per event, false until known (the full menu, never a flash of less).
  private isPreview = false;
  private previewLoadedFor: string | null = null;

  constructor(private router: Router, public translate: TranslateService, private http: HttpClient, private route: ActivatedRoute, private grants: GrantsRepository, private events: EventsRepository) {
    router.events.forEach((event) => {
      if (event instanceof NavigationEnd) {
        this._rebuildMenuItems();
        this._activateMenuDropdown();
        this._scrollElement();
      }
    });
  }

  // Platform super-admin (Firebase Auth `admin: true` claim). Gates the
  // Statistics + AI Usage menu entries to super-admins only.
  isSuperAdmin = false;

  ngOnInit() {
    this._rebuildMenuItems();
    onAuthStateChanged(getAuth(), async (user) => {
      this.uid = user?.uid ?? null;
      try {
        const t = user ? await user.getIdTokenResult() : null;
        this.isSuperAdmin = t?.claims?.['admin'] === true;
      } catch (err) {
        console.error('sidebar: could not read the super-admin claim', err);
        this.isSuperAdmin = false;
      }
      this._rebuildMenuItems();
    });
  }

  private _getEventIdFromRouter(): string | null {
    // Prefer the new canonical route param (/events/:eventId/...).
    let current = this.router.routerState.snapshot.root;
    while (current) {
      const eventId = current.paramMap.get('eventId');
      if (eventId) return eventId;
      current = current.firstChild as any;
    }

    // Legacy fallback (?eventId=...)
    return this.router.routerState.snapshot.root.queryParamMap.get('eventId');
  }

  // Loads the user's grant on the open event once per (event, user), then
  // rebuilds the menu so the reports they were granted appear. A failed read
  // leaves them hidden (rules would refuse their data anyway); the repository
  // logs it.
  private _loadGrantFor(eventId: string | null) {
    const key = eventId && this.uid ? `${eventId}/${this.uid}` : null;
    if (key === this.grantLoadedFor) return;
    this.grantLoadedFor = key;
    this.grant = null;
    if (!eventId || !this.uid) return;
    this.grants.getGrant(eventId, this.uid).then(grant => {
      if (this.grantLoadedFor !== key) return;
      this.grant = grant;
      this._rebuildMenuItems();
    });
  }

  // Loads whether the open event is a preview once per event, then rebuilds the
  // menu. A failed read leaves the full menu; the repository logs it.
  private _loadPreviewFlagFor(eventId: string | null) {
    if (eventId === this.previewLoadedFor) return;
    this.previewLoadedFor = eventId;
    this.isPreview = false;
    if (!eventId) return;
    this.events.getEvent(eventId).then(data => {
      if (this.previewLoadedFor !== eventId) return;
      this.isPreview = data?.['isPreview'] === true;
      this._rebuildMenuItems();
    }).catch(err => console.error('sidebar: could not read the event for the preview flag', err));
  }

  private _rebuildMenuItems() {
    this.eventId = this._getEventIdFromRouter();
    const eventId = this.eventId;
    this._loadGrantFor(eventId);
    this._loadPreviewFlagFor(eventId);

    this.menuItems = visibleMenu(MENU, this.isSuperAdmin, this.grant, this.isPreview)
      .map(item => {
      if (!eventId) {
        return { ...item, queryParams: undefined } as any;
      }

      // Every per-event page declares `useEventId` + a canonical
      // `/events/<slug>` link; rewrite it to `/events/<eventId>/<slug>`.
      // Data-driven off the menu entry itself — a new per-event page needs
      // only a MENU item, never a code branch here. (The prior per-label
      // if-chain silently dropped the eventId for any page missing its
      // branch — that was the Analytics-link bug; per CLAUDE.md we fix the
      // CLASS at the chokepoint, not the one instance.)
      if (item.useEventId && item.link) {
        return { ...item, path: eventScopedPath(item.link, eventId), queryParams: undefined };
      }

      return item;
    });
  }


  ngAfterViewInit() {
    this.menu = new MetisMenu(this.sideMenu.nativeElement);
    this._activateMenuDropdown();
  }

  toggleMenu(event) {
    event.currentTarget.nextElementSibling.classList.toggle('mm-show');
  }

  ngOnChanges() {
    if (!this.isCondensed && this.sideMenu || this.isCondensed) {
      setTimeout(() => {
        this.menu = new MetisMenu(this.sideMenu.nativeElement);
      });
    } else if (this.menu) {
      this.menu.dispose();
    }
  }
  _scrollElement() {
    setTimeout(() => {
      if (document.getElementsByClassName("mm-active").length > 0) {
        const currentPosition = document.getElementsByClassName("mm-active")[0]['offsetTop'];
        if (currentPosition > 500)
          if (this.scrollRef.SimpleBar !== null)
            this.scrollRef.SimpleBar.getScrollElement().scrollTop =
              currentPosition + 300;
      }
    }, 300);
  }

  /**
   * remove active and mm-active class
   */
  _removeAllClass(className) {
    const els = document.getElementsByClassName(className);
    while (els[0]) {
      els[0].classList.remove(className);
    }
  }

  /**
   * Activate the parent dropdown
   */
  _activateMenuDropdown() {
    this._removeAllClass('mm-active');
    this._removeAllClass('mm-show');
    const links = document.getElementsByClassName('side-nav-link-ref');
    let menuItemEl = null;
    // tslint:disable-next-line: prefer-for-of
    const paths = [];
    for (let i = 0; i < links.length; i++) {
      paths.push(links[i]['pathname']);
    }
    var itemIndex = paths.indexOf(window.location.pathname);
    if (itemIndex === -1) {
      const strIndex = window.location.pathname.lastIndexOf('/');
      const item = window.location.pathname.substr(0, strIndex).toString();
      menuItemEl = links[paths.indexOf(item)];
    } else {
      menuItemEl = links[itemIndex];
    }
    if (menuItemEl) {
      menuItemEl.classList.add('active');
      const parentEl = menuItemEl.parentElement;
      if (parentEl) {
        parentEl.classList.add('mm-active');
        const parent2El = parentEl.parentElement.closest('ul');
        if (parent2El && parent2El.id !== 'side-menu') {
          parent2El.classList.add('mm-show');
          const parent3El = parent2El.parentElement;
          if (parent3El && parent3El.id !== 'side-menu') {
            parent3El.classList.add('mm-active');
            const childAnchor = parent3El.querySelector('.has-arrow');
            const childDropdown = parent3El.querySelector('.has-dropdown');
            if (childAnchor) { childAnchor.classList.add('mm-active'); }
            if (childDropdown) { childDropdown.classList.add('mm-active'); }
            const parent4El = parent3El.parentElement;
            if (parent4El && parent4El.id !== 'side-menu') {
              parent4El.classList.add('mm-show');
              const parent5El = parent4El.parentElement;
              if (parent5El && parent5El.id !== 'side-menu') {
                parent5El.classList.add('mm-active');
                const childanchor = parent5El.querySelector('.is-parent');
                if (childanchor && parent5El.id !== 'side-menu') { childanchor.classList.add('mm-active'); }
              }
            }
          }
        }
      }
    }

  }

  /**
   * Initialize
   */
  initialize(): void {
    this.menuItems = MENU.map(item => {
      if (item.useEventId && this.eventId) {
        return {
          ...item,
          link: undefined, // avoid using full encoded string
          routerLink: [item.link],
          queryParams: { [item.queryParamKey]: this.eventId }
        };
      }
      return item;
    });
  }



  /**
   * Returns true or false if given menu item has child or not
   * @param item menuItem
   */
  hasItems(item: MenuItem) {
    return item.subItems !== undefined ? item.subItems.length > 0 : false;
  }
}
